import React, { useState, useEffect } from 'react';
import PocketBase from 'pocketbase';

const pb = new PocketBase('https://bahcarplaza.com');

function MobileCheckoutPage({ setView, selectedCar, setActiveTab }) {
  const [step, setStep] = useState(1);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false); 
  
  const [useWallet, setUseWallet] = useState(false);
  const [paymentMethodType, setPaymentMethodType] = useState('Online');

  useEffect(() => {
    window.scrollTo(0, 0);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!pb.authStore.isValid) { setView('auth'); return; }
    if (!selectedCar) { setView('home'); return; }
    
    const fetchUser = async () => {
      try {
        const user = await pb.collection('users').getOne(pb.authStore.model.id);
        setUserData(user);
        setLoading(false);
      } catch (error) { 
        console.error(error); 
        setLoading(false); 
      }
    };
    fetchUser();
  }, [selectedCar]);

  if (loading || !selectedCar) {
    return <div className="min-h-screen bg-[#f4f7f6] flex justify-center items-center font-sans font-black text-[#002f34]">Loading Checkout...</div>;
  }

  const getVal = (val) => (Array.isArray(val) ? val[0] : val || "");
  const carImage = `https://bahcarplaza.com/api/files/${selectedCar.collectionId}/${selectedCar.id}/${getVal(selectedCar.image)}`;

  // 🚨 Calculations 🚨
  const carPrice = selectedCar.price || 0;
  const platformFee = 999;
  const subTotal = carPrice + platformFee;
  
  const walletBalance = userData?.wallet_balance || 0;
  const walletDeduction = useWallet ? Math.min(walletBalance, subTotal) : 0;
  const finalAmount = subTotal - walletDeduction;

  const tokenLimit = 10000;
  const payableAmount = paymentMethodType === 'COD' && finalAmount > 0 ? Math.min(tokenLimit, finalAmount) : finalAmount;
  const balanceOnDelivery = paymentMethodType === 'COD' ? finalAmount - payableAmount : 0;

  if (finalAmount === 0 && paymentMethodType === 'COD') setPaymentMethodType('Online');

  // 🚨 Handlers (FIXED ROUTING) 🚨
  const handleEditAddress = () => {
    if (setActiveTab) setActiveTab('My Address');
    setView('account');
    // Account পেজের মেইন মেনু হাইড করে ডাইরেক্ট ডিটেইলস পেজ খোলার জন্য ইভেন্ট ফায়ার করা হচ্ছে
    setTimeout(() => window.dispatchEvent(new Event('closeAccountMenu')), 50);
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!userData?.address_line || !userData?.pin_code) {
        alert("Please add a delivery address to proceed.");
        return;
      }
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    
    const fullAddress = `${userData.address_line}, ${userData.landmark ? userData.landmark + ', ' : ''}${userData.city}, ${userData.state} - ${userData.pin_code}`;
    const currentDateTime = new Date().toISOString();

    let dbPaymentMethod = 'Card'; 
    if (finalAmount === 0) dbPaymentMethod = 'Wallet';
    else if (paymentMethodType === 'COD') dbPaymentMethod = 'Cash on Delivery';

    let orderRecord;
    try {
      orderRecord = await pb.collection('orders').create({ 
        user_id: pb.authStore.model.id, 
        car_id: selectedCar.id, 
        amount: carPrice,
        tax_amount: platformFee,
        final_amount: finalAmount,
        payment_status: 'Pending',
        order_status: 'Pending',
        order_date: currentDateTime,
        delivery_address: fullAddress,
        payment_method: dbPaymentMethod
      });
    } catch (e) {
      alert("Failed to initialize order: " + e.message);
      setIsProcessing(false); return;
    }

    // Logic 1: 100% Wallet Payment
    if (finalAmount === 0 || payableAmount === 0) {
      try {
        await pb.collection('orders').update(orderRecord.id, { 
          payment_status: 'Processing', order_status: 'Confirmed', paid_at: currentDateTime,
          transaction_id: `WALLET_TXN_${Date.now()}`, notes: `Paid fully via Wallet balance. Deducted: ₹${walletDeduction.toLocaleString()}`
        });
        
        const newBalance = Number(walletBalance) - Number(walletDeduction);
        const updatedUser = await pb.collection('users').update(pb.authStore.model.id, { wallet_balance: newBalance });
        pb.authStore.save(pb.authStore.token, updatedUser);

        try { await pb.collection('cars').update(selectedCar.id, { status: 'Sold' }); } catch (err) {}

        setPaymentSuccess(true); setIsProcessing(false);
        setTimeout(() => { 
          if (setActiveTab) setActiveTab('My Orders'); 
          setView('account'); 
          setTimeout(() => window.dispatchEvent(new Event('closeAccountMenu')), 50);
        }, 3000);
        return;
      } catch (err) {
        alert("Failed to process wallet payment.");
        setIsProcessing(false); return;
      }
    }

    // Logic 2: Razorpay Payment
    try {
      const response = await fetch('https://bahcarplaza.com/payment/api/razorpay/create-order', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: payableAmount, receiptId: `receipt_${orderRecord.id}` })
      });

      const data = await response.json();
      if (!data.success) { alert("Payment Initialization Failed!"); setIsProcessing(false); return; }

      await pb.collection('orders').update(orderRecord.id, { razorpay_order_id: data.order.id });

      const options = {
        key: data.keyId, amount: data.order.amount, currency: "INR", name: "Bah! Car Plaza",
        description: paymentMethodType === 'COD' ? `Token Payment for ${selectedCar.brand}` : `Payment for ${selectedCar.brand}`,
        image: "https://cdn-icons-png.flaticon.com/512/3202/3202926.png",
        order_id: data.order.id, 
        handler: async (response) => {
          const paymentNotes = paymentMethodType === 'COD' 
            ? `Paid ₹${payableAmount.toLocaleString()} token via Razorpay. Balance ₹${balanceOnDelivery.toLocaleString()} on delivery. Wallet deducted: ₹${walletDeduction.toLocaleString()}`
            : `Paid full balance ₹${payableAmount.toLocaleString()} via Razorpay. Wallet deducted: ₹${walletDeduction.toLocaleString()}`;

          try {
            await pb.collection('orders').update(orderRecord.id, { 
              payment_status: 'Processing', order_status: 'Confirmed', paid_at: new Date().toISOString(),
              transaction_id: response.razorpay_payment_id, notes: paymentNotes
            });
            await pb.collection('cars').update(selectedCar.id, { status: 'Sold' });
            
            if (useWallet && walletDeduction > 0) {
              const newBalance = Number(walletBalance) - Number(walletDeduction);
              const updatedUser = await pb.collection('users').update(pb.authStore.model.id, { wallet_balance: newBalance });
              pb.authStore.save(pb.authStore.token, updatedUser);
            }
          } catch (err) {}

          setPaymentSuccess(true); setIsProcessing(false);
          // 🚨 FIXED ROUTING 🚨
          setTimeout(() => { 
            if (setActiveTab) setActiveTab('My Orders'); 
            setView('account'); 
            setTimeout(() => window.dispatchEvent(new Event('closeAccountMenu')), 50);
          }, 3000);
        },
        modal: { ondismiss: function() { setIsProcessing(false); } },
        prefill: { name: userData?.name || "", email: userData?.email || "", contact: userData?.phone || "" },
        theme: { color: "#002f34" }
      };

      const razorpayObject = new window.Razorpay(options);
      razorpayObject.on('payment.failed', async function () {
        await pb.collection('orders').update(orderRecord.id, { payment_status: 'Failed' });
        alert("❌ Payment Failed. Please try again."); setIsProcessing(false);
      });
      razorpayObject.open();

    } catch (error) {
      alert("Server Error! Could not connect to payment gateway."); setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7f6] flex flex-col font-sans pb-[100px]">
      
      <div className="bg-white p-4 flex items-center gap-4 sticky top-0 z-30 shadow-sm border-b border-gray-100">
        <button onClick={() => step === 2 ? setStep(1) : setView('buy')} className="bg-transparent border-none text-[#002f34] text-2xl font-black cursor-pointer p-0">←</button>
        <h2 className="m-0 font-black text-lg text-[#002f34] uppercase tracking-wide">
          {step === 1 ? 'Review Order' : 'Payment Mode'}
        </h2>
      </div>

      <div className="p-4 flex-1 overflow-y-auto hide-scrollbar">
        
        {/* ==========================================
            🚨 STEP 1: CAR DETAILS & ADDRESS & SUMMARY 🚨
            ========================================== */}
        {step === 1 && (
          <div className="flex flex-col gap-4 animate-slideUp">
            
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex gap-4">
              <img src={carImage} className="w-[100px] h-[75px] rounded-xl object-cover" alt="Car" />
              <div className="flex flex-col justify-center">
                <h3 className="m-0 text-[#002f34] font-black text-[16px] uppercase leading-tight">{selectedCar.brand} {selectedCar.model}</h3>
                <p className="m-0 text-gray-500 font-bold text-[11px] mt-1">{selectedCar.year} • {selectedCar.kilometers_driven?.toLocaleString()} KM • {getVal(selectedCar.fuel_type)}</p>
                <p className="m-0 text-[#ef5e33] font-black text-[16px] mt-2">₹{carPrice.toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="flex justify-between items-center mb-3 border-b border-gray-50 pb-3">
                <h3 className="m-0 text-[#002f34] font-black text-[14px] uppercase">Delivery Address</h3>
                <button onClick={handleEditAddress} className="bg-transparent border-none text-[#ef5e33] font-black text-[11px] cursor-pointer uppercase">Edit ✎</button>
              </div>

              {userData?.address_line ? (
                <div>
                  <h4 className="m-0 text-[#002f34] font-black text-[14px] uppercase mb-1">{userData.name}</h4>
                  <p className="m-0 text-gray-600 font-bold text-[12px] leading-relaxed">
                    {userData.address_line}<br/>
                    {userData.landmark && <>{userData.landmark}<br/></>}
                    {userData.city}, {userData.state} - {userData.pin_code}
                  </p>
                  <p className="m-0 mt-2 text-[#002f34] font-black text-[11px]">📞 +91 {userData.phone}</p>
                </div>
              ) : (
                <div className="bg-[#fff4e6] p-4 rounded-xl text-center">
                  <p className="m-0 text-[#ef5e33] font-bold text-[12px] mb-3">⚠️ Address required for delivery.</p>
                  <button onClick={handleEditAddress} className="bg-[#002f34] text-white border-none px-4 py-2 rounded-lg font-black text-[11px] uppercase">Add Address</button>
                </div>
              )}
            </div>

            {/* 🚨 NEW: Price Summary (Step 1) 🚨 */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="m-0 text-[#002f34] font-black text-[14px] uppercase border-b border-gray-50 pb-3 mb-3">Order Summary</h3>
              <div className="flex justify-between items-center mb-2 font-bold text-gray-500 text-[12px]">
                <span>Car Value</span>
                <span>₹{carPrice.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center mb-3 font-bold text-gray-500 text-[12px]">
                <span>Platform Fee</span>
                <span>₹{platformFee.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center border-t border-dashed border-gray-200 pt-3 font-black text-[#002f34] text-[16px]">
                <span>Total Value</span>
                <span>₹{subTotal.toLocaleString()}</span>
              </div>
            </div>

          </div>
        )}

        {/* ==========================================
            🚨 STEP 2: PAYMENT OPTIONS & SUMMARY 🚨
            ========================================== */}
        {step === 2 && (
          <div className="flex flex-col gap-4 animate-slideUp">
            
            {walletBalance > 0 && (
              <div className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${useWallet ? 'bg-[#e1f9eb] border-[#1db954]' : 'bg-white border-gray-200'}`} onClick={() => setUseWallet(!useWallet)}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={useWallet} readOnly className="w-5 h-5 accent-[#1db954] pointer-events-none" />
                  <div>
                    <h4 className="m-0 text-[#002f34] font-black text-[14px]">Use Wallet Balance</h4>
                    <p className="m-0 text-gray-500 font-bold text-[11px] mt-0.5">Available: ₹{walletBalance.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}

            {finalAmount > 0 && (
              <>
                <h3 className="m-0 mt-2 text-[#002f34] font-black text-[14px] uppercase px-1">Select Payment Mode</h3>
                
                <div className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${paymentMethodType === 'Online' ? 'bg-[#e3f2fd] border-[#1a73e8]' : 'bg-white border-gray-100'}`} onClick={() => setPaymentMethodType('Online')}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethodType === 'Online' ? 'border-[#1a73e8]' : 'border-gray-300'}`}>
                      {paymentMethodType === 'Online' && <div className="w-2.5 h-2.5 bg-[#1a73e8] rounded-full"></div>}
                    </div>
                    <div>
                      <h4 className="m-0 text-[#002f34] font-black text-[14px]">Pay Online (Full)</h4>
                      <p className="m-0 text-gray-500 font-bold text-[10px] mt-0.5">UPI, Cards, NetBanking</p>
                    </div>
                  </div>
                </div>

                <div className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${paymentMethodType === 'COD' ? 'bg-[#fff4e6] border-[#ef5e33]' : 'bg-white border-gray-100'}`} onClick={() => setPaymentMethodType('COD')}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethodType === 'COD' ? 'border-[#ef5e33]' : 'border-gray-300'}`}>
                      {paymentMethodType === 'COD' && <div className="w-2.5 h-2.5 bg-[#ef5e33] rounded-full"></div>}
                    </div>
                    <div>
                      <h4 className="m-0 text-[#002f34] font-black text-[14px]">Cash on Delivery</h4>
                      <p className="m-0 text-[#ef5e33] font-black text-[10px] mt-0.5">Pay ₹10,000 Token Now</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* 🚨 NEW: Payment Summary (Step 2) 🚨 */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mt-2">
              <h3 className="m-0 text-[#002f34] font-black text-[14px] uppercase border-b border-gray-50 pb-3 mb-3">Payment Summary</h3>
              <div className="flex justify-between items-center mb-2 font-bold text-gray-500 text-[12px]">
                <span>Total Value</span>
                <span>₹{subTotal.toLocaleString()}</span>
              </div>
              {useWallet && walletDeduction > 0 && (
                <div className="flex justify-between items-center mb-2 font-black text-[#1db954] text-[12px]">
                  <span>Wallet Applied</span>
                  <span>- ₹{walletDeduction.toLocaleString()}</span>
                </div>
              )}
              {paymentMethodType === 'COD' && finalAmount > 0 && (
                <>
                  <div className="flex justify-between items-center mb-2 font-black text-[#ef5e33] text-[12px]">
                    <span>Token to Pay Now</span>
                    <span>₹{payableAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center font-bold text-gray-500 text-[12px]">
                    <span>Balance on Delivery</span>
                    <span>₹{balanceOnDelivery.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center border-t border-dashed border-gray-200 pt-3 font-black text-[#002f34] text-[16px]">
                <span>Amount to Pay</span>
                <span>₹{payableAmount.toLocaleString()}</span>
              </div>
            </div>

          </div>
        )}

      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-10px_20px_rgba(0,0,0,0.05)] rounded-t-[25px] z-40">
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="m-0 text-gray-500 font-black text-[10px] uppercase tracking-wider mb-1">
              {step === 2 && paymentMethodType === 'COD' ? 'Token Payable' : 'Total Payable'}
            </p>
            <h2 className="m-0 text-[#002f34] font-black text-[22px]">
              ₹{step === 2 ? payableAmount.toLocaleString() : subTotal.toLocaleString()}
            </h2>
          </div>
          <div className="text-right">
            <span className="text-[#1db954] font-black text-[10px] bg-[#e1f9eb] px-2 py-1 rounded-md">100% Safe</span>
          </div>
        </div>

        {step === 1 ? (
          <button onClick={handleNextStep} className="w-full bg-[#ef5e33] text-white py-4 rounded-[15px] font-black text-[14px] uppercase tracking-wide shadow-lg active:scale-95 transition-transform">
            Continue to Payment ➔
          </button>
        ) : (
          <button disabled={isProcessing} onClick={handlePayment} className={`w-full text-white py-4 rounded-[15px] font-black text-[14px] uppercase tracking-wide shadow-lg transition-transform ${isProcessing ? 'bg-gray-400' : (paymentMethodType === 'Online' ? 'bg-[#1a73e8] active:scale-95' : 'bg-[#002f34] active:scale-95')}`}>
            {isProcessing ? 'Processing...' : (payableAmount === 0 ? '✨ Complete Order' : `🔒 Pay ₹${payableAmount.toLocaleString()} Now`)}
          </button>
        )}
      </div>

      {paymentSuccess && (
        <div className="fixed inset-0 bg-[#002f34]/90 z-[9999] flex items-center justify-center p-5 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white w-full max-w-[350px] rounded-[30px] p-8 text-center shadow-2xl animate-popIn">
            <div className="text-[70px] mb-2 animate-bounce">🎉</div>
            <h2 className="m-0 text-[#1db954] font-black text-[24px] uppercase tracking-wide mb-3">Order Successful!</h2>
            <p className="m-0 text-gray-500 font-bold text-[13px] leading-relaxed mb-6">
              Your car has been booked securely. Redirecting to your orders...
            </p>
            <div className="w-10 h-10 border-4 border-gray-100 border-t-[#1db954] rounded-full mx-auto animate-spin"></div>
          </div>
        </div>
      )}

      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-slideUp { animation: slideUp 0.4s ease-out forwards; }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
          @keyframes popIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
          .animate-popIn { animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
          @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
          .animate-bounce { animation: bounce 2s infinite ease-in-out; }
          @keyframes spin { 100% { transform: rotate(360deg); } }
          .animate-spin { animation: spin 1s linear infinite; }
        `}
      </style>
    </div>
  );
}

export default MobileCheckoutPage;